import classNames from 'classnames';
import DataSource from 'devextreme/data/data_source';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import styles from './OrderSummary.module.scss';
import { iRootDispatch, iRootState, select } from '../../../../store/store';
import { OrderForwardData, OrderForwardStepItemKeys } from '../order-forward/OrderForward';
import { OrderReturnData, OrderReturnStepItemKeys } from '../order-return/OrderReturn';
import OrderReturnStepForm, {
    OrderReturnStepFormData,
} from '../order-return-step-form/OrderReturnStepForm';
import OrderForwardStepForm, {
    OrderForwardStepFormData,
} from '../order-forward-step-form/OrderForwardStepForm';
import { orderStepsForwardGridId, orderStepsReturnGridId } from './inspectorOptions';
import DataGrid from 'fulogics-ui-web/dist/layout/dx/DataGrid/DataGrid';
import withInspector from 'fulogics-ui-web/dist/layout/inspector/hoc/withInspector';
import { COSOrderForwardStepStore } from '../../../../store/models/COSOrderForwardStepModel';
import { COSOrderReturnStepStore } from '../../../../store/models/COSOrderReturnStepModel';
import { useMemoDataSource } from 'fulogics-ui-web/dist/layout/hooks/useMemoDataSource';
import { nameof } from 'ts-simple-nameof';
import { Template } from 'devextreme-react/core/template';
import ForwardStepTypeColumnTemplate from '../../../templates/dataGrid/column/forward-step-type/ForwardStepTypeColumnTemplate';
import OrderForwardPlannedStartDateColumnTemplate from '../../../templates/dataGrid/column/order-forward-planned-start-date-column-template/OrderForwardPlannedStartDateColumnTemplate';
import OrderReturnPlannedStartDateColumnTemplate from '../../../templates/dataGrid/column/order-return-planned-start-date-column-template/OrderReturnPlannedStartDateColumnTemplate';

const selection = select((_models) => ({}));

const mapStateToProps = (state: iRootState) =>
    ({
        createForwardStepsCollectionDataSource: (forwardStepsCollectionId?: string) => {
            const opts = COSOrderForwardStepStore.createByCollectionId(forwardStepsCollectionId, {
                defaultOrderByPolicy: 'timetable',
            });
            if (opts) opts.select = OrderForwardStepItemKeys;

            return opts;
        },
        createReturnStepsCollectionDataSource: (returnStepsCollectionId?: string) => {
            const opts = COSOrderReturnStepStore.createByCollectionId(returnStepsCollectionId, {
                defaultOrderByPolicy: 'timetable',
            });
            if (opts) opts.select = OrderReturnStepItemKeys;

            return opts;
        },
        ...(selection(state) as any),
    } as OrderSummaryDisplayOptions);

const mapDispatch = (_dispatch: iRootDispatch, _ownDispatch: OrderSummaryDisplayOptions) =>
    ({
        ..._ownDispatch,
    } as OrderSummaryDisplayOptions);

const OrderSummary = connect(
    mapStateToProps,
    mapDispatch as any
)((props: OrderSummaryProps) => {
    const { t } = useTranslation('OrderSummary');
    const orderStepsForwardDataSource = useMemoDataSource(
        () => props.createForwardStepsCollectionDataSource?.(props.data?.forwardSteps?.id),
        [props.data?.forwardSteps?.id]
    );
    const orderStepsReturnDataSource = useMemoDataSource(
        () => props.createReturnStepsCollectionDataSource?.(props.data?.returnSteps?.id),
        [props.data?.returnSteps?.id]
    );

    const [selectedForwardStep, setSelectedForwardStep] =
        React.useState<OrderForwardStepFormData | undefined>(undefined);
    const [selectedReturnStep, setSelectedReturnStep] =
        React.useState<OrderReturnStepFormData | undefined>(undefined);

    const onOrderStepsForwardGridFocusedRowChanged = (e: any) => {
        const dataRow = e.row && e.row.data;

        if (!!dataRow) {
            setSelectedReturnStep(undefined);
            setSelectedForwardStep(dataRow);
        }
    };

    const onOrderStepsReturnGridFocusedRowChanged = (e: any) => {
        const dataRow = e.row && e.row.data;

        if (!!dataRow) {
            setSelectedForwardStep(undefined);
            setSelectedReturnStep(dataRow);
        }
    };

    const onForwardStepFormFieldDataChanged = useCallback(
        (dataField?: string, value?: any) => {
            if (dataField === nameof<OrderForwardStepFormData>((q) => q.payload)) {
                orderStepsForwardDataSource?.reload();
            }
        },
        [orderStepsForwardDataSource]
    );

    const onReturnStepFormFieldDataChanged = useCallback(
        (dataField?: string, value?: any) => {
            if (dataField === nameof<OrderReturnStepFormData>((q) => q.payload)) {
                orderStepsReturnDataSource?.reload();
            }
        },
        [orderStepsReturnDataSource]
    );

    return (
        <div className={classNames(styles.container, props.className)}>
            <div className={classNames(styles.left)}>
                {!!selectedForwardStep && (
                    <OrderForwardStepForm
                        data={selectedForwardStep}
                        onFieldDataChanged={onForwardStepFormFieldDataChanged}
                        isReadOnly
                    />
                )}
                {!!selectedReturnStep && (
                    <OrderReturnStepForm
                        data={selectedReturnStep}
                        onFieldDataChanged={onReturnStepFormFieldDataChanged}
                        isReadOnly
                    />
                )}
                {!!selectedForwardStep && !!selectedReturnStep && (
                    <label>{t('notSelectedStep')}</label>
                )}
            </div>
            <div className={classNames(styles.right)}>
                <div className={classNames(styles.topRight)}>
                    <div>{t('orderForwardPlacesTitle')}</div>
                    <DataGrid
                        inspectorId={orderStepsForwardGridId}
                        keyExpr="id"
                        dataSource={orderStepsForwardDataSource}
                        focusedRowEnabled
                        focusedRowKey={selectedForwardStep?.id}
                        onFocusedRowChanged={onOrderStepsForwardGridFocusedRowChanged}
                    >
                        <Template name="ReturnStepType" render={ForwardStepTypeColumnTemplate} />
                        <Template
                            name="OrderForwardPlannedStartDateColumn"
                            render={OrderForwardPlannedStartDateColumnTemplate}
                        />
                    </DataGrid>
                </div>
                <div className={classNames(styles.bottomRight)}>
                    <div>{t('orderReturnPlacesTitle')}</div>
                    <DataGrid
                        inspectorId={orderStepsReturnGridId}
                        keyExpr="id"
                        dataSource={orderStepsReturnDataSource}
                        focusedRowEnabled
                        focusedRowKey={selectedReturnStep?.id}
                        onFocusedRowChanged={onOrderStepsReturnGridFocusedRowChanged}
                    >
                        <Template name="ReturnStepType" render={ForwardStepTypeColumnTemplate} />
                        <Template
                            name="OrderReturnPlannedStartDateColumn"
                            render={OrderReturnPlannedStartDateColumnTemplate}
                        />
                    </DataGrid>
                </div>
            </div>
        </div>
    );
});

export default withInspector<OrderSummaryProps>(OrderSummary, {});

export type OrderSummaryProps = {
    data?: OrderSummaryData;
} & OrderSummaryDisplayOptions;

export class OrderSummaryDisplayOptions {
    className?: string;
    //
    createForwardStepsCollectionDataSource?: (forwardStepsCollectionId?: string) => DataSource;
    createReturnStepsCollectionDataSource?: (forwardStepsCollectionId?: string) => DataSource;
}

export type OrderSummaryData = OrderForwardData & OrderReturnData;
{
}

export const OrderSummaryDataKeys = ['id'];
